import { List } from 'immutable';
import { createSelector } from 'reselect';

import { DEPARTMENT_NOT_SET } from './constants';
import { formatRoleOptionsWithDepartments } from './util';
export const getLocationRoles = state =>
  state.getIn(['dailyReview', 'locationRoles']);
export const getLocationIssues = state =>
  state.getIn(['dailyReview', 'locationIssues']);
export const getLocationEmployees = state =>
  state.getIn(['dailyReview', 'locationEmployees']);

export const getDailyReviewLoading = state =>
  state.getIn(['dailyReview', 'isInitialDataLoading']) ||
  state.getIn(['dailyReview', 'isRowDataLoading']);
export const getFirstTimeGuideTooltipOpen = state =>
  state.getIn(['dailyReview', 'firstTimeGuideTooltipOpen']);
export const getFirstTimeGuideModalOpen = state =>
  state.getIn(['dailyReview', 'firstTimeGuideModalOpen']);
export const getShowActionSuccessToast = state =>
  state.getIn(['dailyReview', 'showActionSuccessToast']);
export const getShowActionSuccessToastText = state =>
  state.getIn(['dailyReview', 'actionSuccessToastText']);
export const getRoles = createSelector(getLocationRoles, roles =>
  roles?.reduce((data, role) => {
    let roleValue = role;
    if (role.get('department_name') === DEPARTMENT_NOT_SET) {
      roleValue = role.set('department_name', null);
    }

    data[role.get('id')] = roleValue;
    return data;
  }, {})
);

export const getEmployees = createSelector(getLocationEmployees, employees =>
  employees?.reduce((data, e) => {
    data[e.get('id')] = e;
    return data;
  }, {})
);

export const getRows = state => state.getIn(['dailyReview', 'rows']);

export const getPopulatedRows = createSelector(
  getRows,
  getEmployees,
  getRoles,
  getDailyReviewLoading,
  (rows, employees, roles, isLoading) => {
    if (isLoading) {
      return [];
    }

    const getRole = (row, allRoles) => {
      const role = allRoles[row.get('role_id')];
      return role;
    };

    const formatRows = (jobRows, allRoles, allEmployees) =>
      jobRows.map(row => {
        const user_id = row.get('user_id');
        const employee = allEmployees[user_id];
        const role = getRole(row, allRoles);

        const issues = row.get('issues');
        const assistedClockOut = row.get('assisted_clock_out');
        const assistedClockIn = row.get('assisted_clock_in');

        const extraColumns = {
          id: row.get('id'),
          approved: row.get('approved'),
          locked_message: row.get('locked_message'),
          actual_start_at: row.get('actual_start_at'),
          employee: {
            id: user_id,
            name: employee.get('full_name'),
            initials: employee.get('initials'),
            avatar: employee.get('avatar_url'),
            department: role?.get('department_name'),
            role: role?.get('name') || '',
          },
          issues: issues.filter(
            issue =>
              issue.get('type') !== 'late' &&
              issue.get('cta') !== null &&
              (!issue.get('resolved') || issue.get('type') !== 'auto_clock_out')
          ),
          actions: {
            issues: row
              .get('issues')
              .filter(
                issue =>
                  issue.get('type') !== 'late' &&
                  issue.get('cta') !== null &&
                  (!issue.get('resolved') ||
                    issue.get('type') !== 'auto_clock_out')
              ),
            shiftId: row.get('id'),
            is_assisted_clock_out: !!assistedClockOut,
            is_assisted_clock_in: !!assistedClockIn,
          },
          worked: {
            caption: row.get('caption'),
            duration: row.get('duration'),
            feedback: row.get('feedback'),
            notes: row.get('notes'),
            actual_start_at: row.get('actual_start_at'),
            actual_end_at: row.get('actual_end_at'),
            assisted_end_at: assistedClockOut?.get('end_at'),
            assisted_start_at: assistedClockIn?.get('start_at'),
            assisted_clock_out_status: assistedClockOut?.get('status'),
            assisted_clock_out_comment: assistedClockOut?.get('comment'),
            assisted_clock_out_submitted_at:
              assistedClockOut?.get('submitted_at'),
            assisted_clock_out_past_due: assistedClockOut?.get('past_due'),
            assisted_clock_in_status: assistedClockIn?.get('status'),
            assisted_clock_in_comment: assistedClockIn?.get('comment'),
            assisted_clock_in_submitted_at:
              assistedClockIn?.get('submitted_at'),
            alerts: row
              .get('issues')
              .filter(
                issue =>
                  issue.get('type') === 'late' || issue.get('cta') === null
              ),
          },
        };

        row = row
          .delete('caption')
          .delete('duration')
          .delete('feedback')
          .delete('notes');

        return row.merge(extraColumns);
      });

    const populatedRows = formatRows(rows, roles, employees);

    return populatedRows;
  }
);

export const getDailyReviewFilters = state =>
  state.getIn(['dailyReview', 'filters']);

export const getDailyReviewRowIds = createSelector(
  [getPopulatedRows, getDailyReviewFilters],
  (rows, filters) => {
    const filteredRows = rows.filter(row => {
      // Filter by employee
      const employee = filters.get('employee');
      if (employee && row.getIn(['employee', 'id']) !== employee) {
        return false;
      }
      // Filter by role
      const roles = filters.get('roles')?.toJS();
      if (roles.length > 0 && roles.includes(row.get('role_id')) === false) {
        return false;
      }

      // Filter by issues
      const issues = filters.get('issues')?.toJS();
      if (issues.length > 0) {
        const rowIssues = row.get('issues').map(issue => issue.get('type'));
        if (issues.some(issue => rowIssues.includes(issue)) === false) {
          return false;
        }
      }
      // Filter by review filter
      const reviewFilter = filters.get('reviewFilter') || 'all';
      if (reviewFilter === 'needs_review') {
        // Check if the timecard has any issues (all types)
        const rowIssues = row
          .get('issues')
          .filter(issue => issue.get('type') !== 'assisted_clock_out');
        const hasIssues = rowIssues && rowIssues.size > 0;

        if (!hasIssues) {
          return false;
        }
      }

      // Return true if no filters are applied
      return true;
    });
    return filteredRows.map(row => row.get('id'));
  }
);

export const getDailyReviewFiltersCount = createSelector(
  [getDailyReviewFilters],
  filters => {
    if (filters.toJS) filters = filters.toJS();
    return Object.keys(filters).reduce((acc, key) => {
      if (
        Array.isArray(filters[key]) ? filters[key].length > 0 : filters[key]
      ) {
        // Don't count reviewFilter at all - it's a separate segmented picker
        if (key === 'reviewFilter') {
          return acc;
        }
        acc++;
      }
      return acc;
    }, 0);
  }
);

export const getDailyReviewFilteredEmptyState = createSelector(
  [getPopulatedRows, getDailyReviewFilters, getDailyReviewRowIds],
  (_, filters, filteredRowIds) => {
    const hasFilters =
      filters.get('employee') ||
      filters.get('roles')?.size > 0 ||
      filters.get('issues')?.size > 0;
    const hasRowsButNoResults = filteredRowIds.size === 0;

    return hasFilters && hasRowsButNoResults;
  }
);

export const getDailyReviewNeedsReviewEmptyState = createSelector(
  [getPopulatedRows, getDailyReviewFilters],
  (rows, filters) => {
    const reviewFilter = filters.get('reviewFilter') || 'all';

    if (reviewFilter === 'needs_review') {
      const hasTimecardsWithIssues = rows.some(row => {
        const rowIssues = row.get('issues');
        return rowIssues && rowIssues.size > 0;
      });

      return !hasTimecardsWithIssues;
    }

    return false;
  }
);

export const getDailyReviewRow = createSelector(
  [getPopulatedRows, (_, props) => props.rowId],
  (rows, rowId) => rows.find(row => row.get('id') === rowId)
);

export const getPayrollIneligibleEmployees = state =>
  state.getIn(['dailyReview', 'needsReviewEmployees']);

export const getPayrollIneligibleEmployeesRowIds = createSelector(
  [getPayrollIneligibleEmployees, getDailyReviewFilters],
  (rows, filters) => {
    if (!rows) return List();
    const filteredRows = rows.filter(row => {
      const employee = filters.get('employee');
      if (employee && row.get('id') !== employee) {
        return false;
      }

      // Filter by issues
      const issues = filters.get('issues')?.toJS();
      if (
        issues.length > 0 &&
        !issues.find(issue => issue === 'missing_payroll_details')
      ) {
        return false;
      }

      return true;
    });
    return filteredRows.map(row => row.get('id'));
  }
);
export const getPopulatedPayrollIssueRows = createSelector(
  getPayrollIneligibleEmployees,
  getEmployees,
  getRoles,
  (ineligibleEmployees, employees, roles) => {
    const userIdToRoleIdMap = {};
    Object.values(employees).forEach(employee => {
      const userId = employee.get('user_id');
      const roleId = employee.get('role_id');
      userIdToRoleIdMap[userId] = roleId;
    });

    const populatedEmployees = ineligibleEmployees.map(employee => {
      const userId = employee.get('id');
      const roleId = userIdToRoleIdMap[userId];
      const role = roles[roleId];
      const issues = [
        {
          type: employee.has_check_address_error
            ? 'invalid_address'
            : 'missing_payroll_details',
        },
      ];
      const extraColumns = {
        id: userId,
        employee: {
          id: userId,
          name: employee.get('full_name'),
          initials: employee.get('initials'),
          avatar: employee.get('avatar_url'),
          department: role?.get('department_name'),
          role: role?.get('name') || '',
        },
        issues,
        actions: {
          issues,
        },
      };
      return employee.merge(extraColumns);
    });

    return populatedEmployees;
  }
);
export const getPayrollIneligibleEmployeesRow = createSelector(
  [getPopulatedPayrollIssueRows, (_, props) => props.rowId],
  (rows, rowId) => rows.find(row => row.get('id') === rowId)
);

export const getDailyReviewDate = state => state.getIn(['dailyReview', 'date']);

export const getDailyReviewIssuesCount = createSelector(
  [getPopulatedRows],
  rows => {
    const count = rows.reduce((acc, row) => {
      const filteredIssues = row
        .get('issues')
        .filter(issue => issue.get('type') !== 'assisted_clock_out');
      return acc + filteredIssues.size;
    }, 0);
    return count === 0 ? null : count;
  }
);

export const getEmployeeFilterOptions = createSelector(
  [getLocationEmployees],
  employees => {
    const employeeOptions = employees?.toJS().map(employee => ({
      value: employee.id,
      label: employee.full_name,
    }));
    return employeeOptions || [];
  }
);

export const getRoleFilterOptions = createSelector([getLocationRoles], roles =>
  formatRoleOptionsWithDepartments(roles?.toJS() || [])
);

export const getIssuesFilterOptions = createSelector(
  [getLocationIssues],
  issues => {
    const issueOptions = issues?.toJS().map(issue => ({
      value: issue.type,
      label: issue.label,
    }));
    return issueOptions?.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
  }
);
export const getDailyReviewRowsApprovalStatus = state => {
  const rows = getPopulatedRows(state);
  const approvedRows = rows.filter(row => row.get('approved'));

  const allRowsLocked = rows.size === approvedRows.size;
  return allRowsLocked ? 'locked' : 'unlocked';
};

export const getDailyReviewHasAssistedClockOut = createSelector(
  [getPopulatedRows, getDailyReviewRowIds],
  (rows, visibleRowIds) => {
    const visibleRows = rows.filter(row =>
      visibleRowIds.includes(row.get('id'))
    );
    return visibleRows.some(row =>
      row
        .get('issues')
        ?.some(issue => issue.get('type') === 'assisted_clock_out')
    );
  }
);

export const getDailyReviewHasMissingClockOut = createSelector(
  [getPopulatedRows, getDailyReviewRowIds],
  (rows, visibleRowIds) => {
    const visibleRows = rows.filter(row =>
      visibleRowIds.includes(row.get('id'))
    );
    return visibleRows.some(row =>
      row
        .get('issues')
        ?.some(issue => issue.get('type') === 'missing_clock_out')
    );
  }
);
